
import { App as CapacitorApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { KeepAwake } from '@capacitor-community/keep-awake';
import { useEffect, useState, useCallback } from 'react';
import { BrowserRouter, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import { Modal, Button } from 'react-bootstrap';

import CategoryForm from './components/CategoryForm';
import ContentForm from './components/ContentForm';
import SubCategoryForm from './components/SubCategoryForm';
import GetAllContent from './services/ContentService';
import GetAllSubCategoryComponent from './services/SubCategoryService';

import HomePageService from './visible/HomePageService';
import { ShowOneContentService } from './visible/ShowOneContentService';
import ShowAllContentBySubCategory from './visible/ShowSubCategoryService';

import SearchPage from './visible/ShowSearchService';

import NavBarComponent from './visible/NavBarComponent';
import ShowStaticPage from './visible/ShowStaticPage';
import OneSignal from 'onesignal-cordova-plugin';
import { isMobileApp, ONESIGNAL_APP_ID } from './Constants';
import NotFoundPage from './components/PageNotFound';
import FeedbackForm from './visible/FeedbackForm';
import Admindashboard from './components/AdminDashboard';
import axios from "axios";

axios.defaults.withCredentials = true;

function AppShell() {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isPrintView = /^\/content\/[^/]+\/print$/.test(location.pathname);
  const [showExitModal, setShowExitModal] = useState(false);

  useEffect(() => {
    if (!location.pathname.startsWith('/subcategory/')) {
      window.scrollTo(0, 0);
    }
  }, [location.pathname]);

  const handleMobileBack = useCallback(
    ({ canGoBack } = {}) => {
      if (showExitModal) {
        setShowExitModal(false);
        return;
      }

      const pathname = location.pathname;

      // 1. Homescreen (or feedback overlay): prompt exit confirmation
      if (pathname === '/') {
        setShowExitModal(true);
        return;
      }

      // 2. If history exists, use the original natural navigation flow
      const hasHistory =
        Boolean(canGoBack) ||
        (typeof window !== 'undefined' &&
          window.history?.state?.idx &&
          window.history.state.idx > 0);

      if (hasHistory) {
        window.history.back();
        return;
      }

      // 3. Fallback hierarchical navigation when opened directly without history (deep link / notification)
      const contentMatch = pathname.match(/^\/content\/([^/]+)/);
      if (contentMatch) {
        const contentId = contentMatch[1];
        const cachedContent = queryClient.getQueryData(['content', contentId]);
        const scid =
          cachedContent?.subCategory?.subCategoryId ||
          cachedContent?.subCategory?.id ||
          cachedContent?.subCategoryId;

        if (scid) {
          navigate(`/subcategory/${scid}`);
          return;
        }

        navigate('/');
        return;
      }

      // SubCategory or any other route with no prior history -> navigate to Home
      navigate('/');
    },
    [showExitModal, location.pathname, queryClient, navigate]
  );

  useEffect(() => {
    if (!isMobileApp) {
      return undefined;
    }

    let isMounted = true;
    let backButtonListener;

    const setupBackButton = async () => {
      backButtonListener = await CapacitorApp.addListener(
        'backButton',
        (event) => {
          if (!isMounted) return;
          handleMobileBack(event || {});
        }
      );
    };

    setupBackButton();

    return () => {
      isMounted = false;
      backButtonListener?.remove();
    };
  }, [handleMobileBack]);

  const handleConfirmExit = () => {
    setShowExitModal(false);
    if (CapacitorApp && typeof CapacitorApp.exitApp === 'function') {
      CapacitorApp.exitApp();
    } else if (Capacitor?.Plugins?.App?.exitApp) {
      Capacitor.Plugins.App.exitApp();
    }
  };

  useEffect(() => {
    if (!isMobileApp) {
      return undefined;
    }

    let isMounted = true;
    let appUrlOpenListener;

    const setupAppUrlOpen = async () => {
      appUrlOpenListener = await CapacitorApp.addListener('appUrlOpen', (event) => {
        if (!isMounted) return;
        const slug = event.url.split('.in').pop();
        if (slug) {
          navigate(slug);
        }
      });
    };

    setupAppUrlOpen();

    return () => {
      isMounted = false;
      appUrlOpenListener?.remove();
    };
  }, [navigate]);

  return (
    <>
      {!isPrintView && <NavBarComponent />}
      <Routes>
        {!isMobileApp && (
          <>
            <Route path="/admin/stats" element={<Admindashboard />} />
            <Route path="/admin" element={<GetAllSubCategoryComponent />} />
            <Route path="/admin/content/list/:scid" element={<GetAllContent />} />
            <Route path="/admin/insert/category" element={<CategoryForm />} />
            <Route path="/admin/insert/subcategory" element={<SubCategoryForm />} />
            <Route path="/admin/content" element={<ContentForm />} />
            <Route path="/admin/content/:id" element={<ContentForm />} />
          </>
        )}

        <Route path="/" exact element={<HomePageService />} />
        <Route path="/feedback" element={<HomePageService />} />
        <Route path="/subcategory/:scid" element={<ShowAllContentBySubCategory />} />
        <Route path="/content/:id" element={<ShowOneContentService />} />
        <Route path="/content/:id/print" element={<ShowOneContentService printMode />} />
        <Route path="/search" element={<SearchPage />} />
        <Route path="/page/:pagename" element={<ShowStaticPage />} />
        <Route
          path="*"
          element={<NotFoundPage />}
        />
      </Routes>
      {!isPrintView && <FeedbackForm />}

      {/* Exit Confirmation Dialog for Mobile App on Homescreen */}
      {isMobileApp && (
        <Modal
          show={showExitModal}
          onHide={() => setShowExitModal(false)}
          centered
          className="exit-confirm-modal"
          backdrop="static"
        >
          <Modal.Header
            closeButton
            style={{ borderBottom: '1px solid rgba(122, 13, 13, 0.15)' }}
          >
            <Modal.Title
              style={{
                color: '#7a0d0d',
                fontWeight: 'bold',
                fontSize: '1.15rem',
              }}
            >
              गंगाद्वार से बाहर निकलें?
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="text-center py-4">
            <p
              className="mb-1"
              style={{
                fontSize: '1.05rem',
                color: '#2d3748',
                fontWeight: 500,
              }}
            >
              क्या आप गंगाद्वार ऐप से बाहर निकलना चाहते हैं?
            </p>
            <p className="mb-0 text-muted" style={{ fontSize: '0.88rem' }}>
              Do you want to exit the app?
            </p>
          </Modal.Body>
          <Modal.Footer
            style={{
              borderTop: '1px solid rgba(122, 13, 13, 0.15)',
              justifyContent: 'center',
              gap: '0.75rem',
            }}
          >
            <Button
              variant="outline-secondary"
              onClick={() => setShowExitModal(false)}
              style={{ minWidth: '110px', borderRadius: '8px' }}
            >
              रद्द करें (Cancel)
            </Button>
            <Button
              variant="danger"
              onClick={handleConfirmExit}
              style={{
                minWidth: '110px',
                borderRadius: '8px',
                backgroundColor: '#7a0d0d',
                borderColor: '#7a0d0d',
              }}
            >
              बाहर निकलें (Exit)
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </>
  );
}

function App() {
  // Mobile App Native Lifecycle & KeepAwake
  useEffect(() => {
    if (!isMobileApp) {
      return undefined;
    }

    let isMounted = true;

    const setupMobileApp = async () => {
      try {
        await KeepAwake.keepAwake({ state: true });
      } catch (error) {
        console.error("Error enabling Keep Awake", error);
      }

      if (!isMounted) {
        return;
      }

      OneSignal.Debug.setLogLevel(0);
      OneSignal.initialize(ONESIGNAL_APP_ID);
      OneSignal.Notifications.addEventListener('click', async (e) => {
        const clickData = await e.notification;
        console.log("Notification Clicked : " + clickData);
      });
      OneSignal?.Notifications?.requestPermission?.(true)?.catch?.(() => {});
    };

    setupMobileApp();

    return () => {
      isMounted = false;
      KeepAwake?.allowSleep?.()?.catch?.(() => {});
    };
  }, []);

  // Web Screen Wake Lock for browser users (when !isMobileApp)
  useEffect(() => {
    if (isMobileApp) {
      return undefined;
    }

    let wakeLock = null;
    let isMounted = true;

    const requestWakeLock = async () => {
      try {
        if (
          typeof navigator !== 'undefined' &&
          'wakeLock' in navigator &&
          typeof navigator.wakeLock?.request === 'function' &&
          document.visibilityState === 'visible'
        ) {
          wakeLock = await navigator.wakeLock.request('screen');
        }
      } catch (err) {
        // Silently catch wakeLock refusal (e.g. low battery, browser policy)
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && isMounted) {
        requestWakeLock();
      }
    };

    requestWakeLock();
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      isMounted = false;
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (wakeLock) {
        wakeLock.release?.().catch?.(() => {});
        wakeLock = null;
      }
    };
  }, []);

  return (
    <>
      <BrowserRouter>
        <AppShell />
      </BrowserRouter>
    </>
  );
}

export default App;
